import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { Container, Row, Col, Card, Table, Spinner, Modal, Button, Form, Badge, Tab, Tabs, Accordion, Alert } from "react-bootstrap";
import Header from "./Header";
import { getCachedData, setCachedData } from "../utils/apiCache";
import { ProductContext } from "../context/AppContext";

const MedicalInfo = () => {
    const [medicalData, setMedicalData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState("");
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [isAddMode, setIsAddMode] = useState(false);
    const [formData, setFormData] = useState({
        weight: "",
        height: "",
        allergie: "",
        under_medication: "",
        disease: "",
        precaution: "",
    });
    const [UndermedicationurlPhoto, setUndermedicationurlPhoto] = useState(null);
    const [DiseaseurlPhoto, setDiseaseurlPhoto] = useState(null);
    const [PrecautionurlPhoto, setPrecautionurlPhoto] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [studentId, setStudentId] = useState(null);
    const [classSectionId, setClassSectionId] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [updateForm, setUpdateForm] = useState({});
    const [files, setFiles] = useState({ optional: null, under_medication_url: null, disease_url: null, precaution_url: null });
    const [pendingFields, setPendingFields] = useState({});

    const authtoken = localStorage.getItem("token");
    const { userId } = useContext(ProductContext);

    const SECTIONS = [
        { title: 'Weight', field: 'weight' },
        { title: 'Height', field: 'height' },
        { title: 'Allergie', field: 'allergie' },
        { title: 'Under Medication', field: 'under_medication' },
        { title: 'Disease', field: 'disease' },
        { title: 'Precaution', field: 'precaution' },
        { title: 'Under Medication Document', field: 'under_medication_url' },
        { title: 'Disease Document', field: 'disease_url' },
        { title: 'Precaution Document', field: 'precaution_url' },
    ];

    useEffect(() => {
        fetchStudentId();
        fetchSession();
        fetchMedicalInfo();
    }, []);

    useEffect(() => {
        if (medicalData?.update_requests) {
            const pending = {};
            medicalData.update_requests.forEach(req => {
                if (req.status === 'Pending') {
                    pending[req.updated_field] = true;
                }
            });
            setPendingFields(pending);
        }
    }, [medicalData]);

    const fetchStudentId = async () => {
        try {
            const response = await fetch(
                `https://api.mirai.healthcare/api/student?regs_id=${userId}`,
                {
                    headers: { "x-auth": authtoken },
                }
            );
            const data = await response.json();
            if (data.success && data.message.length > 0) {
                const studentData = data.message[0];
                setStudentId(studentData.id);
                setClassSectionId(studentData.class_section_id);
            }
        } catch (error) {
            console.error("Error fetching student ID:", error);
        }
    };

    const fetchSession = async () => {
        try {
            const response = await fetch("https://api.mirai.healthcare/api/enquiry/user-enquiry", {
                headers: { "x-auth": authtoken },
            });
            const data = await response.json();
            if (data.success && data.data.length > 0) {
                const enquiryData = data.data[0];
                setSessionId(enquiryData.session);
            }
        } catch (error) {
            console.error("Error fetching session ID:", error);
        }
    };

    const fetchMedicalInfo = async () => {
        if (!studentId) return;
        const cacheKey = `medical_info_${studentId}`;
        const cached = getCachedData(cacheKey);
        if (cached) {
            setMedicalData(cached);
            setLoading(false);
            
        }
        if (!cached) setLoading(true);
        try {
            const response = await fetch(
                `https://api.mirai.healthcare/api/student-details/medical?student_id=${studentId}`,
                { headers: { "x-auth": authtoken } }
            );
            const data = await response.json();
            if (data.success && data.message.length > 0) {
                setMedicalData(data.message[0]);
                setCachedData(cacheKey, data.message[0]);
            }
        } catch (error) {
            console.error("Error fetching medical data:", error);
        } finally {
            setLoading(false);
        }
    };

    const isDocField = (field) => field.endsWith('_url');

    const onNewValueChange = (field, value) => {
        setUpdateForm(prev => ({
            ...prev,
            [field]: {
                ...(prev[field] || {}),
                newValue: value
            }
        }));
    };

    const onReasonChange = (field, value) => {
        setUpdateForm(prev => ({
            ...prev,
            [field]: {
                ...(prev[field] || {}),
                reason: value
            }
        }));
    };

    const onFileChange = (field, file) => {
        if (isDocField(field)) {
            setFiles(prev => ({ ...prev, [field]: file }));
        } else {
            setFiles(prev => ({ ...prev, optional: file }));
        }
    };

    const getFileName = (field) => {
        const fileKey = isDocField(field) ? field : 'optional';
        const file = files[fileKey];
        if (file) {
            return <small className="text-muted d-block mt-1">Selected: {file.name}</small>;
        }
        return null;
    };

    const handleUpdatePerField = async (field) => {
        const sectionData = updateForm[field] || { newValue: '', reason: '' };
        if (!sectionData.reason.trim()) {
            alert('Please provide a reason.');
            return;
        }
        if (!isDocField(field) && !sectionData.newValue.trim()) {
            alert('Please provide a new value.');
            return;
        }
        if (isDocField(field) && !files[field]) {
            alert('Please select a new document.');
            return;
        }
        setSubmitting(true);
        const formDataToSend = new FormData();
        formDataToSend.append('updated_field', field);
        formDataToSend.append('user_reason', sectionData.reason);
        if (!isDocField(field)) {
            formDataToSend.append('new_value', sectionData.newValue);
            if (files.optional) {
                formDataToSend.append('optional_document', files.optional);
            }
        } else {
            if (files[field]) {
                formDataToSend.append(field, files[field]);
            }
        }
        try {
            const response = await fetch(`https://api.mirai.healthcare/api/student-details/medical/request/${medicalData.id}`, {
                method: 'POST',
                body: formDataToSend,
                headers: {
                    'x-auth': authtoken,
                },
            });
            const result = await response.json();
            if (response.ok && result.success) {
                alert('Update request submitted successfully!');
                fetchMedicalInfo();
                setUpdateForm(prev => {
                    const newForm = { ...prev };
                    delete newForm[field];
                    return newForm;
                });
                setFiles(prev => {
                    const newFiles = { ...prev };
                    if (isDocField(field)) {
                        delete newFiles[field];
                    } else {
                        newFiles.optional = null;
                    }
                    return newFiles;
                });
            } else {
                alert(result.message || 'Failed to submit update request.');
            }
        } catch (error) {
            console.error(error);
            alert('Error submitting update request.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleShowModal = (link) => {
        setModalContent(link);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setModalContent("");
    };

    const handleShowUpdateModal = (addMode = false) => {
        setIsAddMode(addMode);
        setShowUpdateModal(true);
        setSubmitting(false);
        if (addMode) {
            setFormData({
                weight: "",
                height: "",
                allergie: "",
                under_medication: "",
                disease: "",
                precaution: "",
            });
            setUndermedicationurlPhoto(null);
            setDiseaseurlPhoto(null);
            setPrecautionurlPhoto(null);
        } else {
            setUpdateForm({});
            setFiles({ optional: null, under_medication_url: null, disease_url: null, precaution_url: null });
        }
    };

    const handleCloseUpdateModal = () => {
        setShowUpdateModal(false);
        setIsAddMode(false);
        setFormData({
            weight: "",
            height: "",
            allergie: "",
            under_medication: "",
            disease: "",
            precaution: "",
        });
        setUndermedicationurlPhoto(null);
        setDiseaseurlPhoto(null);
        setPrecautionurlPhoto(null);
        setUpdateForm({});
        setFiles({ optional: null, under_medication_url: null, disease_url: null, precaution_url: null });
    };

    const handleImageClick = (imageUrl) => {
        setSelectedImage(imageUrl);
        setShowImageModal(true);
    };

    const handleCloseImageModal = () => {
        setSelectedImage(null);
        setShowImageModal(false);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Approved':
                return <Badge bg="success">{status}</Badge>;
            case 'Pending':
                return <Badge bg="warning">{status}</Badge>;
            case 'Rejected':
                return <Badge bg="danger">{status}</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    const renderValue = (value) => {
        if (typeof value === 'object' && value?.link) {
            return (
                <img
                    src={value.link}
                    alt="Doc"
                    style={{
                        width: "50px",
                        height: "50px",
                        objectFit: "cover",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                        cursor: "pointer",
                    }}
                    onClick={() => handleImageClick(value.link)}
                />
            );
        }
        return value || "N/A";
    };

    const handleAddSubmit = async () => {
        setSubmitting(true);
        const data = new FormData();

        // Append form data
        Object.keys(formData).forEach((key) => {
            data.append(key, formData[key]);
        });

        // Append dynamically fetched values
        data.append("session_id", sessionId);
        data.append("cls_sec_id", classSectionId);
        data.append("reg_id", userId);
        data.append("student_id", studentId);

        // Append file uploads
        if (UndermedicationurlPhoto) {
            data.append("under_medication_url", UndermedicationurlPhoto);
        }

        if (DiseaseurlPhoto) {
            data.append("disease_url", DiseaseurlPhoto);
        }

        if (PrecautionurlPhoto) {
            data.append("precaution_url", PrecautionurlPhoto);
        }

        try {
            const response = await fetch("https://api.mirai.healthcare/api/student-details/medical", {
                method: "POST",
                body: data,
                headers: {
                    "x-auth": authtoken,
                },
            });

            const result = await response.json();
            console.log("API Response:", result);

            if (result.success) {
                alert("Medical information added successfully!");
                fetchMedicalInfo();
                handleCloseUpdateModal();
            } else {
                alert("Failed to add medical information.");
            }
        } catch (error) {
            console.error("Error uploading:", error);
            alert("Failed to submit medical data.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <Header />
            <Container fluid className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2
              className="page-title fw-bold mb-0"
              style={{ letterSpacing: "0.5px" }}>Medical Information</h2>
                    {medicalData ? (
                        <Button variant="primary" onClick={() => handleShowUpdateModal(false)}>
                            Update Medical Info
                        </Button>
                    ) : (
                        <Button variant="success" onClick={() => handleShowUpdateModal(true)}>
                            Add Medical Info
                        </Button>
                    )}
                </div>

                {medicalData && (
                    <Card className="mb-4 shadow-sm">
                        <Card.Body>
                            <h5 className="fw-bold mb-3">General Details</h5>
                            <Row>
                                <Col md={4}>
                                    <p><strong>Weight:</strong> {medicalData?.weight} kg</p>
                                </Col>
                                <Col md={4}>
                                    <p><strong>Height:</strong> {medicalData?.height} cm</p>
                                </Col>
                                <Col md={4}>
                                    <p><strong>Allergies:</strong> {medicalData?.allergie}</p>
                                </Col>
                                <Col md={4}>
                                    <p><strong>Under Medication:</strong> {medicalData?.under_medication}</p>
                                </Col>
                                <Col md={4}>
                                    <p><strong>Disease:</strong> {medicalData?.disease}</p>
                                </Col>
                                <Col md={4}>
                                    <p><strong>Precaution:</strong> {medicalData?.precaution}</p>
                                </Col>
                            </Row>

                            {/* Document Previews */}
                            <Row className="mt-3">
                                {medicalData?.under_medication_url?.link && (
                                    <Col md={4}>
                                        <p><strong>Under Medication Doc:</strong></p>
                                        <img
                                            src={medicalData.under_medication_url.link}
                                            alt="Under Med"
                                            style={{ width: "100px", height: "100px", objectFit: "cover" }}
                                            onClick={() => handleImageClick(medicalData.under_medication_url.link)}
                                        />
                                    </Col>
                                )}
                                {medicalData?.disease_url?.link && (
                                    <Col md={4}>
                                        <p><strong>Disease Doc:</strong></p>
                                        <img
                                            src={medicalData.disease_url.link}
                                            alt="Disease"
                                            style={{ width: "100px", height: "100px", objectFit: "cover" }}
                                            onClick={() => handleImageClick(medicalData.disease_url.link)}
                                        />
                                    </Col>
                                )}
                                {medicalData?.precaution_url?.link && (
                                    <Col md={4}>
                                        <p><strong>Precaution Doc:</strong></p>
                                        <img
                                            src={medicalData.precaution_url.link}
                                            alt="Precaution"
                                            style={{ width: "100px", height: "100px", objectFit: "cover" }}
                                            onClick={() => handleImageClick(medicalData.precaution_url.link)}
                                        />
                                    </Col>
                                )}
                            </Row>
                        </Card.Body>
                    </Card>
                )}

                <Tabs defaultActiveKey="inspection" id="medical-tabs" className="mb-3">
                    <Tab eventKey="inspection" title="Medical Inspection">
                        {medicalData?.medical_inspection && medicalData?.medical_inspection.length > 0 ? (
                            <Card className="shadow-sm">
                                <Card.Body>
                                    <h5 className="fw-bold mb-3">Medical Inspection History</h5>
                                    <div className="table-responsive">
                                        <Table bordered hover className="text-center">
                                            <thead className="bg-primary text-white">
                                                <tr>
                                                    <th>Description</th>
                                                    <th>Date</th>
                                                    <th>Document</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {medicalData?.medical_inspection?.map((inspection) => (
                                                    <tr key={inspection?.id}>
                                                        <td>{inspection?.descp}</td>
                                                        <td>{inspection?.date ? new Date(inspection?.date).toLocaleDateString() : "N/A"}</td>
                                                        <td>
                                                            {inspection.url ? (
                                                                <Button
                                                                    variant="link"
                                                                    className="p-0 text-decoration-none"
                                                                    onClick={() => handleShowModal(inspection?.url?.link)}
                                                                >
                                                                    View Document
                                                                </Button>
                                                            ) : (
                                                                "N/A"
                                                            )}
                                                        </td>
                                                        <td>{getStatusBadge(inspection?.status)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        ) : (
                            <p>No medical inspection records found.</p>
                        )}
                    </Tab>

                    <Tab eventKey="history" title="Update History">
                        {medicalData?.update_requests && medicalData?.update_requests.length > 0 ? (
                            <Card className="shadow-sm">
                                <Card.Body>
                                    <h5 className="fw-bold mb-3">Medical Update Requests</h5>
                                    <div className="table-responsive">
                                        <Table bordered hover className="text-center">
                                            <thead className="bg-primary text-white">
                                                <tr>
                                                    <th>Updated Field</th>
                                                    <th>Old Value</th>
                                                    <th>New Value</th>
                                                    <th>Status</th>
                                                    <th>User Reason</th>
                                                    <th>Admin Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {medicalData?.update_requests?.map((request) => (
                                                    <tr key={request?.id}>
                                                        <td>{request?.updated_field}</td>
                                                        <td>{renderValue(request?.old_value)}</td>
                                                        <td>{renderValue(request?.new_value)}</td>
                                                        <td>{getStatusBadge(request?.status)}</td>
                                                        <td>{request?.user_reason}</td>
                                                        <td>{request?.admin_reason || "N/A"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        ) : (
                            <p>No update requests found.</p>
                        )}

                        {medicalData?.student_medical_audit && medicalData?.student_medical_audit?.length > 0 && (
                            <>
                                <hr />
                                <h5 className="fw-bold mb-3">Medical Audit History</h5>
                                <div className="table-responsive">
                                    <Table bordered hover className="text-center">
                                        <thead className="bg-secondary text-white">
                                            <tr>
                                                <th>Weight</th>
                                                <th>Height</th>
                                                <th>Allergies</th>
                                                <th>Under Medication</th>
                                                <th>Disease</th>
                                                <th>Precaution</th>
                                                <th>Images</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {medicalData?.student_medical_audit?.map((audit) => (
                                                <tr key={audit?.id}>
                                                    <td>{audit?.weight}</td>
                                                    <td>{audit?.height}</td>
                                                    <td>{audit?.allergie}</td>
                                                    <td>{audit?.under_medication}</td>
                                                    <td>{audit?.disease}</td>
                                                    <td>{audit?.precaution}</td>
                                                    <td>
                                                        <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                                            {audit?.under_medication_url?.link && (
                                                                <img
                                                                    src={audit?.under_medication_url?.link}
                                                                    alt="Under Medication"
                                                                    style={{
                                                                        width: "50px",
                                                                        height: "50px",
                                                                        objectFit: "cover",
                                                                        borderRadius: "4px",
                                                                        border: "1px solid #ddd",
                                                                        cursor: "pointer",
                                                                    }}
                                                                    onClick={() => handleImageClick(audit?.under_medication_url?.link)}
                                                                />
                                                            )}
                                                            {audit?.disease_url?.link && (
                                                                <img
                                                                    src={audit?.disease_url.link}
                                                                    alt="Disease"
                                                                    style={{
                                                                        width: "50px",
                                                                        height: "50px",
                                                                        objectFit: "cover",
                                                                        borderRadius: "4px",
                                                                        border: "1px solid #ddd",
                                                                        cursor: "pointer",
                                                                    }}
                                                                    onClick={() => handleImageClick(audit?.disease_url?.link)}
                                                                />
                                                            )}
                                                            {audit?.precaution_url?.link && (
                                                                <img
                                                                    src={audit?.precaution_url?.link}
                                                                    alt="Precaution"
                                                                    style={{
                                                                        width: "50px",
                                                                        height: "50px",
                                                                        objectFit: "cover",
                                                                        borderRadius: "4px",
                                                                        border: "1px solid #ddd",
                                                                        cursor: "pointer",
                                                                    }}
                                                                    onClick={() => handleImageClick(audit?.precaution_url?.link)}
                                                                />
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>{getStatusBadge(audit?.status)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </>
                        )}
                    </Tab>
                </Tabs>

                <Modal show={showModal} onHide={handleCloseModal} size="xl" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Document Viewer</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <iframe
                            src={modalContent}
                            title="Document Viewer"
                            style={{ width: "100%", height: "600px", border: "none" }}
                        ></iframe>
                    </Modal.Body>
                </Modal>

                <Modal show={showUpdateModal} onHide={handleCloseUpdateModal} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{isAddMode ? "Add" : "Update"} Medical Information</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isAddMode ? (
                            <Form>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Weight (kg)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formData.weight}
                                                onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Height (cm)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formData.height}
                                                onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Allergies</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formData.allergie}
                                                onChange={(e) => setFormData({ ...formData, allergie: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Under Medication</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                value={formData.under_medication}
                                                onChange={(e) => setFormData({ ...formData, under_medication: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Disease</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                value={formData.disease}
                                                onChange={(e) => setFormData({ ...formData, disease: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Precaution</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                value={formData.precaution}
                                                onChange={(e) => setFormData({ ...formData, precaution: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Under Medication Document</Form.Label>
                                            <Form.Control
                                                type="file"
                                                onChange={(e) => setUndermedicationurlPhoto(e.target.files[0])}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Disease Document</Form.Label>
                                            <Form.Control
                                                type="file"
                                                onChange={(e) => setDiseaseurlPhoto(e.target.files[0])}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Precaution Document</Form.Label>
                                            <Form.Control
                                                type="file"
                                                onChange={(e) => setPrecautionurlPhoto(e.target.files[0])}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        ) : (
                            <Accordion defaultActiveKey={['0']}>
                                {SECTIONS.map((section, index) => (
                                    <Accordion.Item eventKey={index.toString()} key={section.field}>
                                        <Accordion.Header>{section.title}</Accordion.Header>
                                        <Accordion.Body>
                                            {pendingFields[section.field] ? (
                                                <Alert variant="warning">
                                                    Your update request is already under review. Please wait for approval before making another update.
                                                </Alert>
                                            ) : (
                                                <div>
                                                    <div className="mb-3">
                                                        <label className="form-label">Old {section.title}</label>
                                                        {isDocField(section.field) ? (
                                                            medicalData?.[section.field]?.link ? (
                                                                <img
                                                                    src={medicalData[section.field].link}
                                                                    alt={`Old ${section.title}`}
                                                                    className="img-thumbnail"
                                                                    style={{ maxWidth: '200px', cursor: 'pointer' }}
                                                                    onClick={() => handleImageClick(medicalData[section.field].link)}
                                                                />
                                                            ) : (
                                                                <p className="text-muted">No document</p>
                                                            )
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={medicalData?.[section?.field] || ''}
                                                                disabled
                                                            />
                                                        )}
                                                    </div>
                                                    {!isDocField(section.field) && (
                                                        <div className="mb-3">
                                                            <label className="form-label">New {section.title}</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={updateForm[section.field]?.newValue || ''}
                                                                onChange={(e) => onNewValueChange(section.field, e.target.value)}
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="mb-3">
                                                        <label className="form-label">{isDocField(section.field) ? `New ${section.title}` : 'Optional Document'}</label>
                                                        <input
                                                            type="file"
                                                            className="form-control"
                                                            onChange={(e) => onFileChange(section.field, e.target.files[0])}
                                                        />
                                                        {getFileName(section.field)}
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="form-label">Reason</label>
                                                        <textarea
                                                            className="form-control"
                                                            rows={3}
                                                            value={updateForm[section.field]?.reason || ''}
                                                            onChange={(e) => onReasonChange(section.field, e.target.value)}
                                                            placeholder="Enter reason for update"
                                                        ></textarea>
                                                    </div>
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={() => handleUpdatePerField(section.field)}
                                                        disabled={submitting}
                                                    >
                                                        {submitting ? 'Updating...' : 'Submit Update Request'}
                                                    </button>
                                                </div>
                                            )}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        {isAddMode && (
                            <>
                                <Button variant="secondary" onClick={handleCloseUpdateModal} className="me-3">
                                    Cancel
                                </Button>
                                <Button variant="primary" onClick={handleAddSubmit} disabled={submitting}>
                                    {submitting ? "Submitting..." : "Add"}
                                </Button>
                            </>
                        )}
                    </Modal.Footer>
                </Modal>

                <Modal show={showImageModal} onHide={handleCloseImageModal} centered size="md">
                    <Modal.Header closeButton>
                        <Modal.Title>Image Viewer</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-center">
                        {selectedImage && (
                            <img
                                src={selectedImage}
                                alt="Selected"
                                style={{ width: "100%", height: "auto", borderRadius: "8px" }}
                            />
                        )}
                    </Modal.Body>
                </Modal>
            </Container>
        </div>
    );
};

export default MedicalInfo;